import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { SkeletonPanel } from "@/components/SkeletonPage";
import HubSpotAccounts from "./HubSpotAccounts";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";
import {
  SectionCard,
  Divider,
  PrimaryButton,
  SecondaryButton,
  FieldInput
} from "@/components/ds";
import { Lightbulb } from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

interface HubSpotConfig {
  id: number;
  siteId: number;
  displayName: string;
  hubId: string;
  portalName: string;
  pushTranscripts: boolean;
  createContactIfMissing: boolean;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked?: string;
  authMethod?: string;
  oauthConnectedAt?: string;
}

const CAPABILITIES = [
  {
    title: "Contact lookup & sync",
    icon: "User",
    items: [
      "Find any HubSpot contact instantly by email address — name, company, lifecycle stage, and open deals returned in seconds",
      "Retrieve the full contact record including custom properties, associated deals, and complete activity timeline",
      "Automatically create a new HubSpot contact when a visitor's email isn't found (configurable per connection)",
      "Push chat transcripts as timestamped Notes directly on the matched contact record"
    ]
  },
  {
    title: "Automated transcript push",
    icon: "Edit",
    items: [
      "When a conversation ends, the full transcript is automatically pushed to HubSpot — no agent action required",
      "Attached as a Note on the matched contact so your sales team has complete chat context",
      "Configurable per connected HubSpot account (toggle in the Accounts tab)"
    ]
  },
  {
    title: "AI workflow tools (29)",
    icon: "Bot",
    items: [
      "29 AI tools available in every workflow and bot node: contacts (5), companies (3), deals & pipeline (7 including quotes), tickets (5), tasks (2), engagement & team (7 including email sequences, form submissions, and meetings)",
      "Create HubSpot quotes and send shareable checkout links directly in chat — close sales without leaving the conversation",
      "Enroll contacts into email sequences mid-chat for automated post-conversation follow-up",
      "No workflow configuration required — the AI decides when to call HubSpot based on conversation context",
      "Multiple HubSpot accounts supported — connect portals for different brands or regions on the same site"
    ]
  }
];

const AI_TOOLS = [
  // Contact Management (5)
  {
    name: "hubspot_search_contact",
    label: "Search contact",
    group: "Contact Management",
    description:
      "Find a HubSpot contact by email or phone. Returns name, company, lifecycle stage, and deal info."
  },
  {
    name: "hubspot_get_contact",
    label: "Get contact details",
    group: "Contact Management",
    description:
      "Fetch full contact record including custom properties, associated deals, and activity history."
  },
  {
    name: "hubspot_update_contact",
    label: "Update contact",
    group: "Contact Management",
    description:
      "Update contact properties such as lifecycle stage, job title, phone, or owner."
  },
  {
    name: "hubspot_create_contact",
    label: "Create contact",
    group: "Contact Management",
    description:
      "Create a new HubSpot contact from visitor data collected during the conversation."
  },
  {
    name: "hubspot_search_contacts",
    label: "Advanced contact search",
    group: "Contact Management",
    description:
      "Multi-filter search across contacts — by name, company, owner, lifecycle stage, or date."
  },
  // Company (3)
  {
    name: "hubspot_search_companies",
    label: "Search companies",
    group: "Company",
    description:
      "Find companies in HubSpot by name or domain. Returns industry, size, and associated contacts."
  },
  {
    name: "hubspot_get_company",
    label: "Get company details",
    group: "Company",
    description:
      "Retrieve full company record including all properties, associated contacts, and linked deals."
  },
  {
    name: "hubspot_get_contact_companies",
    label: "Get contact's companies",
    group: "Company",
    description:
      "List all companies associated with a contact — useful for multi-company contacts."
  },
  // Deals & Pipeline (5)
  {
    name: "hubspot_get_deals",
    label: "Get contact's deals",
    group: "Deals & Pipeline",
    description:
      "List all deals associated with a contact — shows stage, amount, and close date."
  },
  {
    name: "hubspot_search_deals",
    label: "Search deals",
    group: "Deals & Pipeline",
    description: "Find deals by name, stage, pipeline, or associated contact."
  },
  {
    name: "hubspot_get_deal",
    label: "Get deal details",
    group: "Deals & Pipeline",
    description:
      "Retrieve full deal record: amount, pipeline stage, close date, and associated contacts."
  },
  {
    name: "hubspot_update_deal",
    label: "Update deal",
    group: "Deals & Pipeline",
    description:
      "Update a deal's stage, amount, close date, or owner from conversation data."
  },
  {
    name: "hubspot_create_deal",
    label: "Create deal",
    group: "Deals & Pipeline",
    description:
      "Create a new deal in HubSpot and associate it with a contact and company."
  },
  // Tickets & Support (5)
  {
    name: "hubspot_create_ticket",
    label: "Create ticket",
    group: "Tickets & Support",
    description:
      "Create a HubSpot Service Hub support ticket linked to the contact and conversation."
  },
  {
    name: "hubspot_get_ticket",
    label: "Get ticket details",
    group: "Tickets & Support",
    description:
      "Retrieve a support ticket by ID — status, priority, pipeline stage, and description."
  },
  {
    name: "hubspot_get_contact_tickets",
    label: "Get contact's tickets",
    group: "Tickets & Support",
    description:
      "List all support tickets associated with a contact — useful for repeat-issue detection."
  },
  {
    name: "hubspot_search_tickets",
    label: "Search tickets",
    group: "Tickets & Support",
    description: "Find tickets by subject, status, priority, or pipeline stage."
  },
  {
    name: "hubspot_update_ticket",
    label: "Update ticket",
    group: "Tickets & Support",
    description:
      "Update a ticket's status, priority, owner, or pipeline stage from the conversation."
  },
  // Tasks (2)
  {
    name: "hubspot_get_contact_tasks",
    label: "Get contact's tasks",
    group: "Tasks",
    description:
      "Retrieve open and completed tasks associated with a contact for follow-up context."
  },
  {
    name: "hubspot_create_task",
    label: "Create task",
    group: "Tasks",
    description:
      "Create a follow-up task in HubSpot — assigned to an owner with due date and subject."
  },
  // Engagement & Team (4)
  {
    name: "hubspot_add_note",
    label: "Add note to contact",
    group: "Engagement & Team",
    description:
      "Attach a note (e.g. conversation summary or transcript) to a contact's activity timeline."
  },
  {
    name: "hubspot_get_contact_timeline",
    label: "Get contact timeline",
    group: "Engagement & Team",
    description:
      "Retrieve recent engagements on a contact — emails, calls, meetings, and notes."
  },
  {
    name: "hubspot_get_owners",
    label: "Get HubSpot owners",
    group: "Engagement & Team",
    description:
      "List HubSpot users (owners/reps) — use to assign contacts, deals, or tickets to the right person."
  },
  {
    name: "hubspot_create_engagement",
    label: "Log call or meeting",
    group: "Engagement & Team",
    description:
      "Create an engagement record (call or meeting) on a contact to log conversation activity."
  },
  {
    name: "hubspot_get_pipeline_stages",
    label: "Get pipeline stages",
    group: "Deals & Pipeline",
    description:
      "Returns all deal and ticket pipeline stages with their IDs and labels. Use this before updating deal/ticket stages to get the correct stage ID for your pipeline."
  },
  {
    name: "hubspot_create_quote",
    label: "Create quote",
    group: "Deals & Pipeline",
    description:
      "Create a HubSpot quote associated with a deal. Returns a shareable quote link that the AI can send directly in chat — close sales without leaving the conversation."
  },
  {
    name: "hubspot_enroll_sequence",
    label: "Enroll in sequence",
    group: "Engagement & Team",
    description:
      "Enroll a HubSpot contact in an email sequence for automated follow-up. Use to trigger nurture campaigns or post-chat follow-ups automatically from the conversation."
  },
  {
    name: "hubspot_get_form_submissions",
    label: "Get form submissions",
    group: "Contact Management",
    description:
      "Retrieve HubSpot form submission records for a contact. Lets the AI see what a visitor has previously submitted — pricing inquiries, demo requests, support tickets."
  },
  {
    name: "hubspot_get_meetings",
    label: "Get meetings",
    group: "Engagement & Team",
    description:
      "List meetings and calls associated with a HubSpot contact. Shows upcoming and past meeting history so the AI can reference prior touchpoints during the conversation."
  }
];

const AI_TOOL_GROUPS = [
  "Contact Management",
  "Company",
  "Deals & Pipeline",
  "Tickets & Support",
  "Tasks",
  "Engagement & Team"
];

// ─── Field Reference ───────────────────────────────────────────────────────────

const HS_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "email", label: "Email", notes: "Primary identifier" },
      { field: "firstname", label: "First Name", notes: "" },
      { field: "lastname", label: "Last Name", notes: "" },
      { field: "company", label: "Company", notes: "" },
      { field: "phone", label: "Phone", notes: "" },
      { field: "mobilephone", label: "Mobile Phone", notes: "" },
      { field: "jobtitle", label: "Job Title", notes: "" },
      {
        field: "lifecyclestage",
        label: "Lifecycle Stage",
        notes:
          "lead / subscriber / marketingqualifiedlead / salesqualifiedlead / opportunity / customer"
      },
      {
        field: "hs_lead_status",
        label: "Lead Status",
        notes:
          "NEW / OPEN / IN_PROGRESS / OPEN_DEAL / UNQUALIFIED / ATTEMPTED_TO_CONTACT / CONNECTED / BAD_TIMING"
      },
      {
        field: "hubspot_owner_id",
        label: "Owner (User ID)",
        notes: "Numeric HubSpot user ID"
      },
      {
        field: "notes_last_contacted",
        label: "Last Contacted",
        notes: "ISO datetime"
      },
      {
        field: "hs_sales_email_last_replied",
        label: "Last Email Reply",
        notes: "ISO datetime"
      },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Company",
    scopeKey: "company",
    fields: [
      { field: "name", label: "Company Name", notes: "" },
      { field: "domain", label: "Domain", notes: "e.g. acme.com" },
      { field: "industry", label: "Industry", notes: "" },
      { field: "annualrevenue", label: "Annual Revenue", notes: "Numeric" },
      { field: "numberofemployees", label: "Employees", notes: "Numeric" },
      { field: "city", label: "City", notes: "" },
      { field: "country", label: "Country", notes: "" },
      { field: "phone", label: "Phone", notes: "" },
      {
        field: "hubspot_owner_id",
        label: "Owner (User ID)",
        notes: "Numeric HubSpot user ID"
      }
    ]
  },
  {
    scope: "Deal",
    scopeKey: "deal",
    fields: [
      { field: "dealname", label: "Deal Name", notes: "" },
      {
        field: "dealstage",
        label: "Stage (ID)",
        notes:
          "Pipeline stage ID — use Get pipeline stages tool to resolve labels"
      },
      { field: "pipeline", label: "Pipeline (ID)", notes: "Pipeline ID" },
      {
        field: "amount",
        label: "Amount",
        notes: "Numeric, no currency symbol"
      },
      { field: "closedate", label: "Close Date", notes: "ISO datetime" },
      {
        field: "hs_deal_stage_probability",
        label: "Win Probability",
        notes: "0.0 – 1.0"
      },
      {
        field: "hubspot_owner_id",
        label: "Owner (User ID)",
        notes: "Numeric HubSpot user ID"
      },
      { field: "hs_is_closed_won", label: "Closed Won", notes: "Boolean" },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Ticket",
    scopeKey: "ticket",
    fields: [
      { field: "subject", label: "Subject", notes: "" },
      { field: "content", label: "Description", notes: "" },
      { field: "hs_pipeline_stage", label: "Pipeline Stage (ID)", notes: "" },
      {
        field: "hs_ticket_priority",
        label: "Priority",
        notes: "LOW / MEDIUM / HIGH / URGENT"
      },
      { field: "hs_resolution", label: "Resolution", notes: "" },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" },
      {
        field: "hubspot_owner_id",
        label: "Owner (User ID)",
        notes: "Numeric HubSpot user ID"
      }
    ]
  }
];

function HsCopyVariableButton({
  scopeKey,
  field
}: {
  scopeKey: string;
  field: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const variable = `{hubspot.${scopeKey}.${field}}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(variable);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy: ${variable}`}
      aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied
          ? "border-green-300 bg-green-50 text-green-700"
          : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function HubSpotFieldReference() {
  const [activeScope, setActiveScope] = React.useState("Contact");
  const scopeData =
    HS_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    HS_FIELD_REFERENCE[0];

  return (
    <SectionCard
      title="Field Reference"
      description="Standard HubSpot field names and the variable syntax to use in workflow HTTP POST nodes. The agent CRM panel shows contact, company, deal, and ticket data automatically — no configuration needed."
    >
      <>
        {/* What you get callout */}
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 mb-5">
          <div className="grid grid-cols-1 gap-2 text-xs">
            <div className="flex items-start gap-2 text-orange-800">
              <span className="text-orange-700">
                <IconByName name="CheckCircle" className="w-4 h-4" />
              </span>
              <span>
                <strong>Agent sidebar</strong> — automatically shows the matched
                contact, their open deals, tickets, and company during every
                live chat. No mapping config needed.
              </span>
            </div>
            <div className="flex items-start gap-2 text-orange-800">
              <span className="text-orange-700">
                <IconByName name="FileText" className="w-4 h-4" />
              </span>
              <span>
                <strong>HTTP POST variables</strong> — copy any variable below
                and paste into a workflow HTTP POST node to forward HubSpot data
                to another system.
              </span>
            </div>
          </div>
        </div>

        {/* Scope selector */}
        <div className="flex gap-2 mb-4">
          {HS_FIELD_REFERENCE.map((s) => (
            <button
              key={s.scope}
              type="button"
              onClick={() => setActiveScope(s.scope)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activeScope === s.scope
                  ? "bg-orange-500 text-white"
                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}
            >
              {s.scope}
            </button>
          ))}
        </div>

        {/* Example */}
        <SectionCard className="!p-3 !mb-4 !bg-zinc-50 text-xs text-zinc-600">
          <div className="flex items-center justify-between mb-1.5">
            <strong className="text-zinc-700">
              Using these in a workflow HTTP POST node
            </strong>
            <span className="text-zinc-400">
              Click any variable in the table to copy
            </span>
          </div>
          <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{`{
  "contact_email":   "{hubspot.contact.email}",
  "contact_name":    "{hubspot.contact.firstname} {hubspot.contact.lastname}",
  "lifecycle_stage": "{hubspot.contact.lifecyclestage}",
  "deal_name":       "{hubspot.deal.dealname}",
  "deal_value":      "{hubspot.deal.amount}",
  "ticket_subject":  "{hubspot.ticket.subject}"
}`}</pre>
        </SectionCard>

        {/* Field table */}
        <div className="rounded-xl border border-zinc-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-zinc-800 text-white text-xs font-semibold">
            {scopeData.scope} fields
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    HubSpot property
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Friendly name
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Variable to copy
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {scopeData.fields.map((f, i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                  >
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                      {f.field}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                    <td className="px-3 py-2">
                      <HsCopyVariableButton
                        scopeKey={scopeData.scopeKey}
                        field={f.field}
                      />
                    </td>
                    <td className="px-3 py-2 text-zinc-400 italic text-[10px]">
                      {f.notes || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-3">
          These are standard HubSpot CRM properties available on every portal.
          Custom properties use the same variable syntax — replace the field
          name with your custom property internal name.
        </p>
      </>
    </SectionCard>
  );
}

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div className="mb-4">
      <SectionCard
        title="What's included"
        description={`${CAPABILITIES.length} feature areas available on every HubSpot connection`}
        action={
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className="text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
          >
            {open ? "Hide" : "Show"}
            <svg
              className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path d="M8 10.5L3 5.5H13L8 10.5Z" />
            </svg>
          </button>
        }
      >
        {open && (
          <>
            <Divider />
            <div className="space-y-2 mt-4">
              {CAPABILITIES.map((cap, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-100 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSection(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-600">
                        <IconByName name={cap.icon} className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-medium text-zinc-700">
                        {cap.title}
                      </span>
                    </div>
                    <svg
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                    </svg>
                  </button>
                  {openSections.has(i) && (
                    <div className="px-4 pb-3 space-y-1.5">
                      <Divider />
                      {cap.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-2 pt-1.5">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                          <span className="text-xs text-zinc-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ── Standard HubSpot contact fields always shown in the agent sidebar ──
const HS_STANDARD_FIELDS: { fieldName: string; label: string; type: string }[] =
  [
    { fieldName: "firstname", label: "First Name", type: "String" },
    { fieldName: "lastname", label: "Last Name", type: "String" },
    { fieldName: "email", label: "Email", type: "String" },
    { fieldName: "company", label: "Company", type: "String" },
    { fieldName: "phone", label: "Phone Number", type: "String" },
    { fieldName: "jobtitle", label: "Job Title", type: "String" },
    {
      fieldName: "lifecyclestage",
      label: "Lifecycle Stage",
      type: "Enumeration"
    },
    { fieldName: "hs_lead_status", label: "Lead Status", type: "Enumeration" }
  ];

interface HsSidebarField {
  fieldName: string;
  label: string;
  groupName?: string;
}

interface HsDiscoveredProperty {
  name: string;
  label: string;
  groupName: string;
}

function HsVariableChip({ fieldName }: { fieldName: string }) {
  const [copied, setCopied] = useState(false);
  const variable = `{hubspot.contact.${fieldName}}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(variable);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy: ${variable}`}
      aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied
          ? "border-green-300 bg-green-50 text-green-700"
          : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function HsSidebarFieldsTab() {
  const api = useApi();
  const [customFields, setCustomFields] = useState<HsSidebarField[]>([]);
  const [properties, setProperties] = useState<HsDiscoveredProperty[]>([]);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedProp, setSelectedProp] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("HubSpot/data/sidebar-fields");
        if (resp.ok) {
          const data = await resp.json();
          setCustomFields(Array.isArray(data?.fields) ? data.fields : []);
        }
      } catch {
        /* safe default */
      }
    })();
  }, [api.messaging]);

  const filteredProperties = filter
    ? properties.filter(
        (p) =>
          p.name.includes(filter.toLowerCase()) ||
          p.label.toLowerCase().includes(filter.toLowerCase()) ||
          p.groupName?.toLowerCase().includes(filter.toLowerCase())
      )
    : properties;

  async function loadProperties() {
    setLoadingProperties(true);
    try {
      const resp = await api.messaging.get("HubSpot/data/properties");
      if (resp.ok) {
        const data = await resp.json();
        setProperties(Array.isArray(data?.properties) ? data.properties : []);
        setPropertiesLoaded(true);
      }
    } catch {
      /* non-fatal */
    } finally {
      setLoadingProperties(false);
    }
  }

  function addField() {
    if (!selectedProp) return;
    const prop = properties.find((p) => p.name === selectedProp);
    if (!prop) return;
    const label = customLabel.trim() || prop.label;
    if (customFields.some((f) => f.fieldName === selectedProp)) return;
    setCustomFields((prev) => [
      ...prev,
      { fieldName: selectedProp, label, groupName: prop.groupName }
    ]);
    setSelectedProp("");
    setCustomLabel("");
    setFilter("");
  }

  function removeField(fieldName: string) {
    setCustomFields((prev) => prev.filter((f) => f.fieldName !== fieldName));
  }

  async function save() {
    setSaving(true);
    try {
      await api.messaging.post("HubSpot/data/sidebar-fields", {
        fields: customFields
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Where it shows */}
      <SectionCard
        title="Field Mapping — HubSpot"
        description="Configure which HubSpot contact properties appear in the agent conversation panel. Standard fields are always shown. Add any custom or extra contact properties from your HubSpot portal."
      >
        <>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-2">
            <div className="text-xs font-semibold text-blue-800 mb-2">
              Where these fields appear
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-md border border-blue-100 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500 shrink-0" />
                  <span className="text-[10px] font-semibold text-zinc-700">
                    HubSpot panel (agent sidebar)
                  </span>
                </div>
                <div className="border-b border-zinc-100 mb-2 pb-1 flex gap-2 text-[9px] font-medium">
                  <span className="border-b-2 border-orange-500 text-orange-700 pb-0.5">
                    Contact
                  </span>
                  <span className="text-zinc-400">Deals</span>
                </div>
                <div className="space-y-1">
                  {[
                    "Sarah Johnson",
                    "VP of Engineering",
                    "Acme Corp • sarah@acme.com"
                  ].map((v) => (
                    <div key={v} className="text-[9px] text-zinc-600">
                      {v}
                    </div>
                  ))}
                  <div className="mt-1.5 pt-1.5">
                    <Divider />
                    <div className="text-[8px] uppercase text-zinc-400 font-semibold tracking-wide mb-1 mt-1.5">
                      Custom fields
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-zinc-500">HubSpot Score</span>
                      <span className="font-medium text-zinc-700">82</span>
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-zinc-500">Region</span>
                      <span className="font-medium text-zinc-700">
                        Northeast
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-xs text-blue-800">
                <p>
                  <strong>Contact fields</strong> → appear when a visitor's
                  email matches a HubSpot contact.
                </p>
                <p>
                  <strong>Variable syntax</strong> for HTTP POST workflow nodes:{" "}
                  <code className="bg-blue-100 px-1 rounded text-[10px]">
                    &#123;hubspot.contact.fieldname&#125;
                  </code>
                </p>
                <p>
                  Custom properties use the same syntax — replace{" "}
                  <code className="bg-blue-100 px-1 rounded text-[10px]">
                    fieldname
                  </code>{" "}
                  with the property's internal name (snake_case).
                </p>
              </div>
            </div>
          </div>
        </>
      </SectionCard>

      {/* Field picker */}
      <SectionCard title="Custom Properties">
        <div>
          {/* Standard fields */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Standard fields
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                Always shown
              </span>
            </div>
            <div className="rounded-xl border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Field
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Label shown to agent
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Variable (copy for HTTP POST)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {HS_STANDARD_FIELDS.map((f) => (
                    <tr key={f.fieldName} className="bg-white">
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                        {f.fieldName}
                      </td>
                      <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                      <td className="px-3 py-2">
                        <HsVariableChip fieldName={f.fieldName} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Custom fields */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Custom fields
              </div>
              {customFields.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                  {customFields.length} added
                </span>
              )}
            </div>
            {customFields.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-200 p-5 text-center text-xs text-zinc-400">
                No custom fields added yet. Discover your HubSpot contact
                properties below and add any you want agents to see.
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Field
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Label shown to agent
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Variable
                      </th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {customFields.map((f) => (
                      <tr key={f.fieldName} className="bg-white">
                        <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                          {f.fieldName}
                        </td>
                        <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                        <td className="px-3 py-2">
                          <HsVariableChip fieldName={f.fieldName} />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeField(f.fieldName)}
                            aria-label={`Remove ${f.label}`}
                            className="text-zinc-400 hover:text-red-500 transition-colors"
                          >
                            <svg
                              viewBox="0 0 12 12"
                              width="12"
                              height="12"
                              fill="none"
                            >
                              <path
                                d="M2 2L10 10M10 2L2 10"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Property discovery picker */}
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 mb-5">
            <div className="text-xs font-semibold text-orange-800 mb-3">
              Add a custom contact field
            </div>
            {!propertiesLoaded ? (
              <PrimaryButton
                onClick={loadProperties}
                disabled={loadingProperties}
              >
                {loadingProperties
                  ? "Discovering properties…"
                  : "Discover contact properties from HubSpot"}
              </PrimaryButton>
            ) : (
              <div className="space-y-2">
                <FieldInput
                  type="text"
                  placeholder="Filter properties by name or group…"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setSelectedProp("");
                  }}
                />
                <div className="flex gap-2">
                  <FieldInput
                    as="select"
                    value={selectedProp}
                    onChange={(e) => {
                      setSelectedProp(e.target.value);
                      const found = filteredProperties.find(
                        (p) => p.name === e.target.value
                      );
                      if (found && !customLabel) setCustomLabel(found.label);
                    }}
                    size={Math.min(filteredProperties.length + 1, 7)}
                  >
                    <option value="">— select a property —</option>
                    {filteredProperties.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.label}
                        {p.label !== p.name ? ` (${p.name})` : ""}
                        {p.groupName ? ` [${p.groupName}]` : ""}
                      </option>
                    ))}
                  </FieldInput>
                </div>
                <div className="flex gap-2">
                  <FieldInput
                    type="text"
                    placeholder="Friendly label for agents (optional — defaults to property display name)"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                  />
                  <PrimaryButton onClick={addField} disabled={!selectedProp}>
                    + Add field
                  </PrimaryButton>
                </div>
                <p className="text-[10px] text-orange-700">
                  {properties.length} contact properties discovered from your
                  HubSpot portal.
                  {filter &&
                    filteredProperties.length !== properties.length &&
                    ` Showing ${filteredProperties.length} matching "${filter}".`}
                </p>
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex items-center gap-3">
            <PrimaryButton onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save sidebar fields"}
            </PrimaryButton>
            {saved && (
              <span className="text-sm text-green-600 font-medium">
                Saved — agents see the updated fields immediately.
              </span>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

export default function HubSpot() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState<HubSpotConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "config"
    | "skills"
    | "sidebar-fields"
    | "field-reference"
    | "log"
  >("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [showConnectedBanner, setShowConnectedBanner] = useState(
    searchParams.get("connected") === "true"
  );
  const [oauthError, setOAuthError] = useState<string | null>(
    searchParams.get("error")
  );

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("HubSpot/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  async function togglePushTranscripts(config: HubSpotConfig) {
    try {
      const resp = await api.admin.put(`HubSpot/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, pushTranscripts: updated.pushTranscripts }
            : c
        )
      );
    } catch {
      // ignore — config list stays at its last-known-good state
    }
  }

  // Auto-dismiss connected banner after 5 seconds
  useEffect(() => {
    if (!showConnectedBanner) return;
    // Remove ?connected=true from the URL without re-navigating
    setSearchParams(
      (prev) => {
        prev.delete("connected");
        return prev;
      },
      { replace: true }
    );
    const timer = setTimeout(() => setShowConnectedBanner(false), 5000);
    return () => clearTimeout(timer);
  }, [showConnectedBanner, setSearchParams]);

  // Remove ?error from URL
  useEffect(() => {
    if (!oauthError) return;
    setSearchParams(
      (prev) => {
        prev.delete("error");
        return prev;
      },
      { replace: true }
    );
  }, [oauthError, setSearchParams]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "config"}
        onClick={() => setActiveTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "skills"}
        onClick={() => setActiveTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "sidebar-fields"}
        onClick={() => setActiveTab("sidebar-fields")}
      >
        Field Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "field-reference"}
        onClick={() => setActiveTab("field-reference")}
      >
        Field Reference
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="HubSpot" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="hubspot"
          hubspotConfigId={configs.find((c) => c.enabled)?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* OAuth connected success banner */}
      {showConnectedBanner && (
        <div className="flex items-center justify-between gap-3 bg-green-50 border border-green-300 rounded-xl px-4 py-3 mb-4 text-green-800 text-sm font-medium">
          <span>✓ HubSpot connected! Your portal is now linked to Velaro.</span>
          <button
            type="button"
            onClick={() => setShowConnectedBanner(false)}
            className="text-green-600 hover:text-green-800 text-lg leading-none px-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* OAuth error banner */}
      {oauthError && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3 mb-4 text-red-800 text-sm font-medium">
          <span>
            HubSpot connection failed: {oauthError.replace(/_/g, " ")}. Please
            try again.
          </span>
          <button
            type="button"
            onClick={() => setOAuthError(null)}
            className="text-red-600 hover:text-red-800 text-lg leading-none px-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {activeTab === "overview" && (
        <div>
          <CapabilitiesPanel />
          {hasActiveConfig && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-800 mb-0.5">
                    Deploy the chat widget to your site
                  </div>
                  <div className="text-xs text-blue-600">
                    Your HubSpot integration is connected — grab your embed code
                    from Deployments to go live.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/chat_window"
                    className="text-xs px-3 py-1.5 rounded-md text-blue-700 hover:text-blue-900 hover:underline transition"
                  >
                    Chat window
                  </Link>
                  <Link
                    to="/deployment"
                    className="text-xs px-3 py-1.5 rounded-md bg-(--ds-primary) hover:opacity-90 text-white transition"
                  >
                    Get embed code →
                  </Link>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> You can also create a{" "}
                  <strong>Knowledge Base Q&A</strong> page widget from{" "}
                  <Link
                    to="/deployment"
                    className="underline hover:text-blue-900"
                  >
                    Deployments → Page Widgets
                  </Link>{" "}
                  for a lightweight, AI-only Q&A experience — no live agent
                  needed.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "config" && (
        <div>
          {loading ? (
            <SkeletonPanel rows={4} />
          ) : (
            <>
              <div className="mb-6">
                <HubSpotAccounts
                  configs={configs}
                  onDelete={(id) =>
                    setConfigs((prev) => prev.filter((c) => c.id !== id))
                  }
                  onRefresh={fetchConfigs}
                />
              </div>

              {/* Transcript Auto-Push */}
              {configs.length > 0 && (
                <SectionCard title="Transcript Auto-Push" className="mb-4">
                  <div className="text-sm text-zinc-500 mb-4">
                    When enabled, the full chat transcript is automatically
                    pushed to HubSpot as a Note on the matched contact after
                    every conversation ends.
                  </div>
                  <div className="space-y-3">
                    {configs.map((config) => (
                      <div
                        key={config.id}
                        className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0"
                      >
                        <div>
                          <div className="text-sm font-medium text-zinc-700">
                            {config.displayName ||
                              config.portalName ||
                              "HubSpot"}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {config.pushTranscripts
                              ? "Transcript pushed as a Note after each conversation"
                              : "Transcript push is off for this connection"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePushTranscripts(config)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                          aria-label={
                            config.pushTranscripts
                              ? "Disable transcript push"
                              : "Enable transcript push"
                          }
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                  {configs.some((c) => c.pushTranscripts && c.enabled) && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                      Auto-push active — transcripts will appear on the matched
                      HubSpot contact after each chat.
                    </div>
                  )}
                </SectionCard>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "skills" && (
        <div>
          {loading ? (
            <SkeletonPanel rows={4} />
          ) : (
            <SectionCard
              title="AI Tools"
              description="29 tools available to any AI workflow node on this site. No configuration required — the AI decides when to call HubSpot based on conversation context."
              action={
                hasActiveConfig ? (
                  <LaunchWizardButton onClick={() => setShowWizard(true)} />
                ) : undefined
              }
              className="mb-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                >
                  {hasActiveConfig ? "Active" : "No active connection"}
                </span>
              </div>

              <Divider />
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                    Available AI tools
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                    29 tools
                  </span>
                </div>
                {AI_TOOL_GROUPS.map((group) => (
                  <div key={group} className="mb-4">
                    <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">
                      {group}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                        <div
                          key={tool.name}
                          className={`flex gap-3 p-3 rounded-md border ${hasActiveConfig ? "border-green-200 bg-green-50" : "border-zinc-200 bg-zinc-50"}`}
                        >
                          <div
                            className={`mt-0.5 w-4 h-4 rounded-full shrink-0 flex items-center justify-center ${hasActiveConfig ? "bg-green-500" : "bg-zinc-300"}`}
                          >
                            {hasActiveConfig && (
                              <svg
                                viewBox="0 0 10 10"
                                width="8"
                                height="8"
                                fill="none"
                              >
                                <path
                                  d="M1.5 5L3.5 7L8.5 3"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-zinc-700">
                              {tool.label}
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              {tool.description}
                            </div>
                            <code className="text-[10px] text-zinc-400 mt-1 block">
                              {tool.name}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Divider />
                <div className="pt-8">
                  <SkillControlsPanel integration="hubspot" skills={AI_TOOLS} />
                </div>
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {activeTab === "sidebar-fields" && <HsSidebarFieldsTab />}

      {activeTab === "field-reference" && <HubSpotFieldReference />}

      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="HubSpot" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingHubSpot() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
